/**
 * Rax Integration Setup Popup
 *
 * Appears when the user wants to configure a channel/integration.
 * Provides guided setup with instructions and form fields.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

export type IntegrationFieldType = "text" | "password" | "select" | "phone";

export interface IntegrationField {
  key: string;
  label: string;
  type: IntegrationFieldType;
  placeholder?: string;
  required?: boolean;
  hint?: string;
  options?: Array<{ value: string; label: string }>; // For select type
}

export interface PollingOption {
  value: string;
  label: string;
  intervalMinutes: number;
  checksPerDay: number;
  estimatedCostPerDay: number; // in cents
  description: string;
}

// Cost estimates based on using a lightweight model (Haiku/GPT-4o-mini) for polling checks
// ~$0.0001 per check (100 input tokens + 20 output tokens at Haiku pricing)
// Full agent only invoked when message found - that cost is separate
export const POLLING_OPTIONS: PollingOption[] = [
  {
    value: "5m",
    label: "Every 5 minutes",
    intervalMinutes: 5,
    checksPerDay: 288,
    estimatedCostPerDay: 3, // ~$0.03/day with cheap model
    description: "Near real-time responses",
  },
  {
    value: "15m",
    label: "Every 15 minutes",
    intervalMinutes: 15,
    checksPerDay: 96,
    estimatedCostPerDay: 1, // ~$0.01/day
    description: "Quick responses, very low cost",
  },
  {
    value: "30m",
    label: "Every 30 minutes (recommended)",
    intervalMinutes: 30,
    checksPerDay: 48,
    estimatedCostPerDay: 0.5, // ~$0.005/day
    description: "Balanced frequency, minimal cost",
  },
  {
    value: "1h",
    label: "Every hour",
    intervalMinutes: 60,
    checksPerDay: 24,
    estimatedCostPerDay: 0.25, // ~$0.0025/day
    description: "Hourly check-ins, negligible cost",
  },
  {
    value: "4h",
    label: "Every 4 hours",
    intervalMinutes: 240,
    checksPerDay: 6,
    estimatedCostPerDay: 0.06, // ~$0.0006/day
    description: "Infrequent checks, essentially free",
  },
  {
    value: "off",
    label: "Manual only",
    intervalMinutes: 0,
    checksPerDay: 0,
    estimatedCostPerDay: 0,
    description: "No automatic polling - you trigger checks",
  },
];

export interface IntegrationConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  instructions: string[];
  fields: IntegrationField[];
  docsUrl?: string;
  testEndpoint?: string;
  supportsPolling?: boolean;
  /** Save as secret instead of channel config - used for tool/capability integrations */
  saveAsSecret?: boolean;
  /** Secret type for saveAsSecret mode */
  secretType?: string;
  /** Environment variable name for saveAsSecret mode */
  secretName?: string;
}

// Channel integration configurations
export const INTEGRATION_CONFIGS: Record<string, IntegrationConfig> = {
  // === FOUNDATIONAL INTEGRATIONS ===

  kernel: {
    id: "kernel",
    name: "Kernel Browser",
    icon: "\u{1F310}", // 🌐
    description:
      "Browser automation - your agent can browse the web, fill forms, and interact with any website",
    instructions: [
      "Sign up at kernel.dev to get your API key",
      "Navigate to Settings > API Keys in the dashboard",
      "Create a new API key and copy it",
      "Your agent will be able to browse any website and perform actions",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Kernel API Key",
        type: "password",
        placeholder: "kernel_...",
        required: true,
        hint: "Found in Kernel dashboard under Settings > API Keys",
      },
    ],
    docsUrl: "https://docs.kernel.dev",
    testEndpoint: "/api/rax/integrations/kernel/test",
    supportsPolling: false,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "KERNEL_API_KEY",
  },

  imessage: {
    id: "imessage",
    name: "iMessage (Sendblue)",
    icon: "\u{1F4AC}", // 💬
    description: "Send and receive iMessages - reach your agent from anywhere via text",
    instructions: [
      "Sign up at sendblue.co and get your API credentials",
      "Find your API Key and Secret in the Sendblue dashboard",
      "Your assigned phone number is shown in the dashboard under 'Lines'",
      "Messages will be sent from this number to your contacts",
    ],
    fields: [
      {
        key: "apiKey",
        label: "API Key",
        type: "text",
        placeholder: "e.g., e834f3e5eb66ad16...",
        required: true,
        hint: "Found in Sendblue dashboard under API settings",
      },
      {
        key: "apiSecret",
        label: "API Secret",
        type: "password",
        placeholder: "Your Sendblue API secret",
        required: true,
        hint: "Keep this secret - don't share it",
      },
      {
        key: "fromNumber",
        label: "Phone Number",
        type: "phone",
        placeholder: "+1234567890",
        required: true,
        hint: "Your Sendblue-assigned number (check dashboard > Lines)",
      },
    ],
    docsUrl: "https://sendblue.co/docs",
    testEndpoint: "/api/rax/channels/imessage/test",
    supportsPolling: true,
  },

  resend: {
    id: "resend",
    name: "Resend Email",
    icon: "\u{1F4E8}", // 📨
    description:
      "Send transactional emails - notifications, summaries, and reports from your agent",
    instructions: [
      "Sign up at resend.com and verify your email domain",
      "Navigate to API Keys in the dashboard",
      "Create a new API key with sending permissions",
      "Your agent can now send emails on your behalf",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Resend API Key",
        type: "password",
        placeholder: "re_...",
        required: true,
        hint: "Found in Resend dashboard under API Keys",
      },
      {
        key: "fromEmail",
        label: "From Email",
        type: "text",
        placeholder: "agent@yourdomain.com",
        required: true,
        hint: "Must be from a verified domain in Resend",
      },
      {
        key: "fromName",
        label: "From Name",
        type: "text",
        placeholder: "My Agent",
        required: false,
        hint: "Display name for sent emails",
      },
    ],
    docsUrl: "https://resend.com/docs",
    testEndpoint: "/api/rax/integrations/resend/test",
    supportsPolling: false,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "RESEND_API_KEY",
  },

  github: {
    id: "github",
    name: "GitHub",
    icon: "\u{1F4BB}", // 💻
    description: "Repository access - your agent can read code, create PRs, and manage issues",
    instructions: [
      "Go to github.com/settings/tokens",
      "Generate a new token (classic) with repo and workflow scopes",
      "For fine-grained tokens, select specific repositories and permissions",
      "Copy the token - it won't be shown again",
    ],
    fields: [
      {
        key: "token",
        label: "Personal Access Token",
        type: "password",
        placeholder: "ghp_... or github_pat_...",
        required: true,
        hint: "Classic token (ghp_) or fine-grained token (github_pat_)",
      },
      {
        key: "defaultOwner",
        label: "Default Owner/Org",
        type: "text",
        placeholder: "your-username or org-name",
        required: false,
        hint: "Optional: Default repository owner for operations",
      },
    ],
    docsUrl:
      "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token",
    testEndpoint: "/api/rax/integrations/github/test",
    supportsPolling: false,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "GITHUB_PAT",
  },

  telegram: {
    id: "telegram",
    name: "Telegram",
    icon: "\u{1F4E8}", // 📨
    description: "Connect your Telegram bot to Rax",
    instructions: [
      "Message @BotFather on Telegram to create a bot",
      "Use /newbot command and follow the prompts",
      "Copy the bot token BotFather gives you",
      "Start a chat with your bot and send a message to get your Chat ID",
    ],
    fields: [
      {
        key: "botToken",
        label: "Bot Token",
        type: "password",
        placeholder: "123456789:ABC-DEF1234ghIkl-zyx57W2v1u123ew11",
        required: true,
        hint: "The token from @BotFather",
      },
      {
        key: "chatId",
        label: "Chat ID",
        type: "text",
        placeholder: "e.g., 123456789",
        required: false,
        hint: "Optional: Your personal chat ID for direct messages",
      },
    ],
    docsUrl: "https://core.telegram.org/bots",
    testEndpoint: "/api/rax/channels/telegram/test",
    supportsPolling: true,
  },
  email: {
    id: "email",
    name: "Email",
    icon: "\u{1F4E7}", // 📧
    description: "Connect email via IMAP/SMTP",
    instructions: [
      "Use an app-specific password (not your regular password)",
      "For Gmail: Enable 2FA, then create an App Password",
      "For Outlook: Create an app password in security settings",
      "IMAP must be enabled in your email settings",
    ],
    fields: [
      {
        key: "email",
        label: "Email Address",
        type: "text",
        placeholder: "you@gmail.com",
        required: true,
      },
      {
        key: "appPassword",
        label: "App Password",
        type: "password",
        placeholder: "Your app-specific password",
        required: true,
        hint: "Not your regular password - use an app password",
      },
      {
        key: "provider",
        label: "Email Provider",
        type: "select",
        required: true,
        options: [
          { value: "gmail", label: "Gmail" },
          { value: "outlook", label: "Outlook / Office 365" },
          { value: "icloud", label: "iCloud" },
          { value: "yahoo", label: "Yahoo" },
          { value: "fastmail", label: "Fastmail" },
          { value: "protonmail", label: "ProtonMail" },
        ],
      },
    ],
    docsUrl: "https://support.google.com/accounts/answer/185833",
    testEndpoint: "/api/rax/channels/email/test",
    supportsPolling: true,
  },

  // === AGENT ONBOARDING INTEGRATIONS ===

  slack: {
    id: "slack",
    name: "Slack",
    icon: "\u{1F4AC}", // 💬
    description: "Send messages to your team - your agent can communicate via Slack",
    instructions: [
      "Go to api.slack.com/apps and create a new app",
      "Choose 'From scratch' and select your workspace",
      "Under OAuth & Permissions, add chat:write and channels:read scopes",
      "Install the app to your workspace and copy the Bot User OAuth Token",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Bot User OAuth Token",
        type: "password",
        placeholder: "xoxb-...",
        required: true,
        hint: "Found in OAuth & Permissions after installing the app",
      },
      {
        key: "defaultChannel",
        label: "Default Channel",
        type: "text",
        placeholder: "#general",
        required: false,
        hint: "Optional: Default channel for messages",
      },
    ],
    docsUrl: "https://api.slack.com/start/quickstart",
    testEndpoint: "/api/rax/integrations/slack/test",
    supportsPolling: true,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "SLACK_BOT_TOKEN",
  },

  discord: {
    id: "discord",
    name: "Discord",
    icon: "\u{1F3AE}", // 🎮
    description: "Chat in your Discord servers - your agent can send and receive messages",
    instructions: [
      "Go to discord.com/developers/applications and create a new application",
      "Navigate to the Bot tab and click 'Add Bot'",
      "Under Token, click 'Copy' to get your bot token",
      "Invite the bot to your server using OAuth2 URL Generator",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Bot Token",
        type: "password",
        placeholder: "Your Discord bot token",
        required: true,
        hint: "Found in the Bot tab of your Discord application",
      },
      {
        key: "guildId",
        label: "Server (Guild) ID",
        type: "text",
        placeholder: "123456789012345678",
        required: false,
        hint: "Optional: Default server ID (right-click server > Copy ID)",
      },
    ],
    docsUrl: "https://discord.com/developers/docs/getting-started",
    testEndpoint: "/api/rax/integrations/discord/test",
    supportsPolling: true,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "DISCORD_BOT_TOKEN",
  },

  notion: {
    id: "notion",
    name: "Notion",
    icon: "\u{1F4DD}", // 📝
    description: "Take notes and document - your agent can read and write to Notion pages",
    instructions: [
      "Go to notion.so/my-integrations and create a new integration",
      "Name it (e.g., 'Rax Agent') and select your workspace",
      "Copy the Internal Integration Secret",
      "Share the pages/databases you want the agent to access with your integration",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Integration Secret",
        type: "password",
        placeholder: "secret_...",
        required: true,
        hint: "Found on your integration's settings page",
      },
      {
        key: "defaultPageId",
        label: "Default Page ID",
        type: "text",
        placeholder: "abc123...",
        required: false,
        hint: "Optional: Default page for new notes (from page URL)",
      },
    ],
    docsUrl: "https://developers.notion.com/docs/getting-started",
    testEndpoint: "/api/rax/integrations/notion/test",
    supportsPolling: false,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "NOTION_API_KEY",
  },

  google_calendar: {
    id: "google_calendar",
    name: "Google Calendar",
    icon: "\u{1F4C5}", // 📅
    description: "Manage your schedule - your agent can view and create calendar events",
    instructions: [
      "Go to console.cloud.google.com and create a new project (or use existing)",
      "Enable the Google Calendar API in the API Library",
      "Create OAuth 2.0 credentials (or Service Account for full access)",
      "Download the credentials JSON file and paste the contents below",
    ],
    fields: [
      {
        key: "apiKey",
        label: "Credentials JSON",
        type: "password",
        placeholder: '{"type": "service_account", ...}',
        required: true,
        hint: "The full contents of your credentials.json file",
      },
      {
        key: "calendarId",
        label: "Calendar ID",
        type: "text",
        placeholder: "primary or your@email.com",
        required: false,
        hint: "Optional: Defaults to 'primary' calendar",
      },
    ],
    docsUrl: "https://developers.google.com/calendar/api/quickstart/js",
    testEndpoint: "/api/rax/integrations/google_calendar/test",
    supportsPolling: false,
    saveAsSecret: true,
    secretType: "custom_api_key",
    secretName: "GOOGLE_CALENDAR_CREDENTIALS",
  },
};

export interface IntegrationSetupDraft {
  channelId: string;
  sourceMessage?: string;
}

@customElement("rax-integration-setup-popup")
export class RaxIntegrationSetupPopup extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .popup-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-4);
        animation: fadeIn 0.2s ease-out;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .popup {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg), var(--shadow-glow);
        max-width: 520px;
        width: 100%;
        max-height: 85vh;
        overflow-y: auto;
        animation: slideUp 0.25s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(24px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .popup-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
      }
      
      .popup-icon {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-md);
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-gold));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
      }
      
      .popup-title {
        flex: 1;
      }
      
      .popup-title h3 {
        margin: 0;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .popup-title p {
        margin: var(--space-1) 0 0;
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      .popup-close {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        padding: var(--space-2);
        font-size: var(--font-xl);
        line-height: 1;
        transition: color var(--transition-fast);
      }
      
      .popup-close:hover {
        color: var(--text-primary);
      }
      
      .popup-body {
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      
      .instructions {
        background: var(--bg-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        border-left: 3px solid var(--rax-yellow);
      }
      
      .instructions-title {
        font-weight: 600;
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
        font-size: var(--font-sm);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .instructions-list {
        margin: 0;
        padding-left: var(--space-4);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.6;
      }
      
      .instructions-list li {
        margin-bottom: var(--space-1);
      }
      
      .docs-link {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        margin-top: var(--space-2);
        font-size: var(--font-xs);
        color: var(--rax-yellow);
        text-decoration: none;
      }
      
      .docs-link:hover {
        text-decoration: underline;
      }
      
      .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .field label {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }
      
      .field label .required {
        color: #ef4444;
      }
      
      .field input,
      .field select {
        background: var(--bg-subtle);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-primary);
        transition: border-color var(--transition-fast);
        width: 100%;
        box-sizing: border-box;
      }
      
      .field input:focus,
      .field select:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 2px var(--accent-glow);
      }
      
      .field input::placeholder {
        color: var(--text-tertiary);
      }
      
      .field-hint {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .polling-section {
        background: var(--bg-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        border: 1px solid var(--border-color);
      }
      
      .polling-title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .polling-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-3);
      }
      
      .polling-options {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .polling-option {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .polling-option:hover {
        border-color: var(--text-tertiary);
      }
      
      .polling-option.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.08);
      }
      
      .polling-option input[type="radio"] {
        margin-top: 2px;
        accent-color: var(--rax-yellow);
      }
      
      .polling-option-content {
        flex: 1;
      }
      
      .polling-option-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-1);
      }
      
      .polling-option-label {
        font-weight: 600;
        color: var(--text-primary);
        font-size: var(--font-sm);
      }
      
      .polling-option-cost {
        font-size: var(--font-xs);
        color: var(--rax-yellow);
        font-weight: 600;
      }
      
      .polling-option-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .cost-summary {
        margin-top: var(--space-3);
        padding: var(--space-3);
        background: rgba(255, 208, 0, 0.1);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      .cost-summary-row {
        display: flex;
        justify-content: space-between;
        color: var(--text-secondary);
      }
      
      .cost-summary-row strong {
        color: var(--text-primary);
      }
      
      .popup-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .footer-left {
        display: flex;
        gap: var(--space-2);
      }
      
      .footer-right {
        display: flex;
        gap: var(--space-2);
      }
      
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        font-weight: 600;
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
      }
      
      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .btn-primary:hover {
        background: var(--rax-gold);
      }
      
      .btn-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .btn-secondary {
        background: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-secondary);
      }
      
      .btn-secondary:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
      
      .btn-ghost {
        background: transparent;
        color: var(--text-tertiary);
      }
      
      .btn-ghost:hover {
        color: var(--text-primary);
      }
      
      .error-message {
        background: rgba(239, 68, 68, 0.14);
        border: 1px solid rgba(239, 68, 68, 0.55);
        color: #fca5a5;
        padding: var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      .success-message {
        background: rgba(34, 197, 94, 0.14);
        border: 1px solid rgba(34, 197, 94, 0.55);
        color: #86efac;
        padding: var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .test-status {
        font-size: var(--font-sm);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .test-status.testing {
        color: var(--text-tertiary);
      }
      
      .test-status.success {
        color: #86efac;
      }
      
      .test-status.error {
        color: #fca5a5;
      }
      
      @media (max-width: 640px) {
        .popup-overlay {
          align-items: flex-end;
          padding: 0;
        }
      
        .popup {
          max-height: 90vh;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      
        .popup-footer {
          flex-direction: column;
        }
      
        .footer-left,
        .footer-right {
          width: 100%;
        }
      
        .footer-right {
          justify-content: flex-end;
        }
      }
    `,
  ];

  @property({ type: Object })
  draft: IntegrationSetupDraft | null = null;

  @state()
  private formValues: Record<string, string> = {};

  @state()
  private saving = false;

  @state()
  private testing = false;

  @state()
  private testResult: { success: boolean; message: string } | null = null;

  @state()
  private error: string | null = null;

  @state()
  private selectedPollingFrequency: string = "30m"; // Default to balanced option

  private get config(): IntegrationConfig | null {
    if (!this.draft?.channelId) return null;
    return INTEGRATION_CONFIGS[this.draft.channelId] ?? null;
  }

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("draft") && this.draft) {
      // Reset form when draft changes
      this.formValues = {};
      this.error = null;
      this.testResult = null;

      // Set default values for select fields
      const config = this.config;
      if (config) {
        for (const field of config.fields) {
          if (field.type === "select" && field.options?.length) {
            this.formValues[field.key] = field.options[0].value;
          }
        }
      }
    }
  }

  override render() {
    if (!this.draft || !this.config) {
      return nothing;
    }

    const config = this.config;

    return html`
      <div class="popup-overlay" @click=${this.handleOverlayClick}>
        <div class="popup" @click=${(e: Event) => e.stopPropagation()}>
          <div class="popup-header">
            <div class="popup-icon">${config.icon}</div>
            <div class="popup-title">
              <h3>Set up ${config.name}</h3>
              <p>${config.description}</p>
            </div>
            <button class="popup-close" @click=${this.handleCancel} aria-label="Close">&times;</button>
          </div>

          <div class="popup-body">
            <div class="instructions">
              <div class="instructions-title">Setup Instructions</div>
              <ol class="instructions-list">
                ${config.instructions.map((instruction) => html`<li>${instruction}</li>`)}
              </ol>
              ${
                config.docsUrl
                  ? html`
                    <a class="docs-link" href=${config.docsUrl} target="_blank" rel="noopener">
                      View documentation &rarr;
                    </a>
                  `
                  : nothing
              }
            </div>

            ${config.fields.map((field) => this.renderField(field))}

            ${config.supportsPolling ? this.renderPollingSection() : nothing}

            ${this.error ? html`<div class="error-message">${this.error}</div>` : nothing}
            ${
              this.testResult?.success
                ? html`<div class="success-message">\u{2705} ${this.testResult.message}</div>`
                : nothing
            }
          </div>

          <div class="popup-footer">
            <div class="footer-left">
              ${
                config.testEndpoint
                  ? html`
                    <button
                      class="btn btn-secondary"
                      ?disabled=${this.testing || this.saving || !this.hasRequiredFields()}
                      @click=${this.handleTest}
                    >
                      ${this.testing ? "Testing..." : "Test Connection"}
                    </button>
                  `
                  : nothing
              }
            </div>
            <div class="footer-right">
              <button class="btn btn-ghost" @click=${this.handleCancel}>Cancel</button>
              <button
                class="btn btn-primary"
                ?disabled=${this.saving || !this.hasRequiredFields()}
                @click=${this.handleSave}
              >
                ${this.saving ? "Saving..." : "Save & Enable"}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderField(field: IntegrationField) {
    const value = this.formValues[field.key] ?? "";

    if (field.type === "select") {
      return html`
        <div class="field">
          <label>
            ${field.label}
            ${
              field.required
                ? html`
                    <span class="required">*</span>
                  `
                : nothing
            }
          </label>
          <select
            .value=${value}
            @change=${(e: Event) => this.updateField(field.key, (e.target as HTMLSelectElement).value)}
          >
            ${field.options?.map(
              (opt) =>
                html` <option value=${opt.value} ?selected=${value === opt.value}>${opt.label}</option> `,
            )}
          </select>
          ${field.hint ? html`<div class="field-hint">${field.hint}</div>` : nothing}
        </div>
      `;
    }

    return html`
      <div class="field">
        <label>
          ${field.label}
          ${
            field.required
              ? html`
                  <span class="required">*</span>
                `
              : nothing
          }
        </label>
        <input
          type=${field.type === "password" ? "password" : "text"}
          .value=${value}
          placeholder=${field.placeholder ?? ""}
          @input=${(e: Event) => this.updateField(field.key, (e.target as HTMLInputElement).value)}
        />
        ${field.hint ? html`<div class="field-hint">${field.hint}</div>` : nothing}
      </div>
    `;
  }

  private updateField(key: string, value: string) {
    this.formValues = { ...this.formValues, [key]: value };
    this.error = null;
    this.testResult = null;
  }

  private renderPollingSection() {
    const selectedOption = POLLING_OPTIONS.find((o) => o.value === this.selectedPollingFrequency);
    const monthlyEstimate = selectedOption
      ? ((selectedOption.estimatedCostPerDay * 30) / 100).toFixed(2)
      : "0.00";

    return html`
      <div class="polling-section">
        <div class="polling-title">
          \u{1F504} Message Polling Frequency
        </div>
        <div class="polling-description">
          How often should Rax check for new messages? More frequent checks mean faster responses but higher costs.
        </div>

        <div class="polling-options">
          ${POLLING_OPTIONS.map(
            (option) => html`
            <label
              class="polling-option ${this.selectedPollingFrequency === option.value ? "selected" : ""}"
              @click=${() => (this.selectedPollingFrequency = option.value)}
            >
              <input
                type="radio"
                name="polling-frequency"
                .value=${option.value}
                ?checked=${this.selectedPollingFrequency === option.value}
              />
              <div class="polling-option-content">
                <div class="polling-option-header">
                  <span class="polling-option-label">${option.label}</span>
                  <span class="polling-option-cost">
                    ${
                      option.estimatedCostPerDay === 0
                        ? "Free"
                        : `~$${(option.estimatedCostPerDay / 100).toFixed(2)}/day`
                    }
                  </span>
                </div>
                <div class="polling-option-desc">${option.description}</div>
              </div>
            </label>
          `,
          )}
        </div>

        ${
          selectedOption && selectedOption.estimatedCostPerDay > 0
            ? html`
              <div class="cost-summary">
                <div class="cost-summary-row">
                  <span>Polling cost (lightweight model):</span>
                  <strong>~$${monthlyEstimate}/month</strong>
                </div>
                <div class="cost-summary-row" style="margin-top: 4px; font-size: 12px; color: var(--text-tertiary);">
                  <span>${selectedOption.checksPerDay} checks/day using Haiku/mini model</span>
                </div>
                <div class="cost-summary-row" style="margin-top: 4px; font-size: 12px; color: var(--text-tertiary);">
                  <span>Note: Responding to messages uses your main agent (separate cost)</span>
                </div>
              </div>
            `
            : selectedOption?.value === "off"
              ? html`
                  <div class="cost-summary" style="background: rgba(100, 100, 100, 0.1)">
                    <div class="cost-summary-row">
                      <span>Manual mode - no automatic polling costs</span>
                    </div>
                    <div
                      class="cost-summary-row"
                      style="margin-top: 4px; font-size: 12px; color: var(--text-tertiary)"
                    >
                      <span>You can trigger checks manually or via automation rules</span>
                    </div>
                  </div>
                `
              : nothing
        }
      </div>
    `;
  }

  private hasRequiredFields(): boolean {
    if (!this.config) return false;
    for (const field of this.config.fields) {
      if (field.required && !this.formValues[field.key]?.trim()) {
        return false;
      }
    }
    return true;
  }

  private handleOverlayClick(e: Event) {
    if (e.target === e.currentTarget) {
      this.handleCancel();
    }
  }

  private handleCancel() {
    this.dispatchEvent(new CustomEvent("cancel"));
  }

  private async handleTest() {
    if (!this.config?.testEndpoint || this.testing) return;

    this.testing = true;
    this.error = null;
    this.testResult = null;

    try {
      const response = await raxApi.request<{ message?: string }>(
        "POST",
        this.config.testEndpoint,
        this.formValues,
      );

      if (response.success) {
        this.testResult = {
          success: true,
          message: (response.data as any)?.message ?? "Connection successful!",
        };
      } else {
        this.error = response.error ?? "Connection test failed";
      }
    } catch (err) {
      console.error("[IntegrationSetup] Test error:", err);
      this.error = err instanceof Error ? err.message : "Connection test failed";
    } finally {
      this.testing = false;
    }
  }

  private async handleSave() {
    if (!this.config || this.saving || !this.hasRequiredFields()) return;

    this.saving = true;
    this.error = null;

    try {
      // Handle saveAsSecret mode for tool/capability integrations
      if (this.config.saveAsSecret) {
        // Get the API key/token value from form
        const secretValue = this.formValues.apiKey || this.formValues.token || "";
        const secretName = this.config.secretName || `${this.config.id.toUpperCase()}_API_KEY`;

        // Store as secret
        const response = await raxApi.storeSecret({
          type: this.config.secretType || "custom_api_key",
          name: secretName,
          value: secretValue,
          metadata: {
            source: "integration_setup",
            integrationId: this.config.id,
            // Store additional fields as metadata
            ...Object.fromEntries(
              Object.entries(this.formValues).filter(
                ([key]) => key !== "apiKey" && key !== "token",
              ),
            ),
          },
        });

        if (!response.success) {
          this.error = response.error ?? "Failed to save integration";
          return;
        }

        this.dispatchEvent(
          new CustomEvent("saved", {
            detail: {
              channelId: this.config.id,
              channelName: this.config.name,
              isIntegration: true,
            },
          }),
        );
        return;
      }

      // Channel config mode - include polling configuration if supported
      const payload = {
        ...this.formValues,
        ...(this.config.supportsPolling
          ? {
              pollingFrequency: this.selectedPollingFrequency,
              pollingIntervalMinutes:
                POLLING_OPTIONS.find((o) => o.value === this.selectedPollingFrequency)
                  ?.intervalMinutes ?? 0,
            }
          : {}),
      };

      const response = await raxApi.request<unknown>(
        "POST",
        `/api/rax/channels/config/${this.config.id}`,
        payload,
      );

      if (!response.success) {
        this.error = response.error ?? "Failed to save configuration";
        return;
      }

      this.dispatchEvent(
        new CustomEvent("saved", {
          detail: {
            channelId: this.config.id,
            channelName: this.config.name,
          },
        }),
      );
    } catch (err) {
      console.error("[IntegrationSetup] Save error:", err);
      this.error = err instanceof Error ? err.message : "Failed to save configuration";
    } finally {
      this.saving = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-integration-setup-popup": RaxIntegrationSetupPopup;
  }
}
